import React from 'react';
import {
  AlertCircle, ArrowLeft, ArrowRight, ArrowUpRight, Check, CircuitBoard,
  Eye, EyeOff, Github, KeyRound, Loader2, LockKeyhole, ShieldCheck, UserRound,
} from 'lucide-react';
import { appLogoUrl } from '../assets';
import './LoginGatewayPageV3.css';

const API_BASE = import.meta.env.VITE_API_URL || '';

interface LoginGatewayPageV3Props {
  authError?: string | null;
  isSubmitting?: boolean;
  onBack?: () => void;
  onLogin: (input: { identifier: string; password: string }) => void | Promise<void>;
  onTryDemo?: (prompt: string) => void;
  version?: string;
}

export function LoginGatewayPageV3({
  authError, isSubmitting = false, onBack, onLogin, version,
}: LoginGatewayPageV3Props) {
  const fieldId = React.useId();
  const identifierRef = React.useRef<HTMLInputElement>(null);
  const passwordRef = React.useRef<HTMLInputElement>(null);
  const submittingRef = React.useRef(false);
  const [identifier, setIdentifier] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [showPassword, setShowPassword] = React.useState(false);
  const [capsLock, setCapsLock] = React.useState(false);
  const [pending, setPending] = React.useState(false);
  const [fieldErrors, setFieldErrors] = React.useState({ identifier: '', password: '' });
  const [submitError, setSubmitError] = React.useState('');
  const [appVersion, setAppVersion] = React.useState('');
  const busy = isSubmitting || pending;
  const error = submitError || authError;

  React.useEffect(() => {
    const controller = new AbortController();
    void fetch(`${API_BASE}/api/public/version`, { signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) return;
        const data = await response.json();
        if (!controller.signal.aborted && typeof data.version === 'string') {
          setAppVersion(data.version.trim());
        }
      }).catch(() => {});
    return () => controller.abort();
  }, []);

  const displayVersion = appVersion || version || '';
  const versionLabel = displayVersion && (/^v/i.test(displayVersion) ? displayVersion : `v${displayVersion}`);

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy || submittingRef.current) return;
    const nextErrors = {
      identifier: identifier.trim() ? '' : '请输入账号或邮箱',
      password: password ? '' : '请输入登录密码',
    };
    setFieldErrors(nextErrors);
    setSubmitError('');
    if (nextErrors.identifier || nextErrors.password) {
      (nextErrors.identifier ? identifierRef : passwordRef).current?.focus();
      return;
    }
    submittingRef.current = true;
    setPending(true);
    try {
      await onLogin({ identifier: identifier.trim(), password });
    } catch (cause) {
      setSubmitError(cause instanceof Error ? cause.message : '登录失败，请稍后重试。');
    } finally {
      submittingRef.current = false;
      setPending(false);
    }
  }

  function updateCapsLock(event: React.KeyboardEvent<HTMLInputElement>) {
    setCapsLock(event.getModifierState('CapsLock'));
  }

  return (
    <main className="realplc-login" lang="zh-CN">
      <div className="rl-login-layout">
        <section className="rl-login-story" aria-labelledby={`${fieldId}-story`}>
          <header className="rl-login-brand-row">
            <div className="rl-login-brand">
              <img src={appLogoUrl} alt="" width="44" height="44" />
              <div><span>RealPLC</span><small>AI FOR PLC ENGINEERING</small></div>
            </div>
            <span className="rl-login-edition">ENGINEERING AI <i /> REAL WORKFLOW</span>
          </header>

          <div className="rl-login-story-content">
            <p className="rl-login-kicker"><span /> 从 AI 灵感，到可靠工程</p>
            <h2 id={`${fieldId}-story`}>让每一次<br />控制逻辑的诞生，<br /><em>都经得起验证。</em></h2>
            <p className="rl-login-description">从需求、审批到代码与原生编译诊断。<br />让 PLC 工程有上下文、有过程，也有依据。</p>

            <div className="rl-login-workflow" aria-label="PLC 工程流程示意">
              <div className="rl-login-workflow-heading">
                <span><CircuitBoard size={16} aria-hidden="true" /> 输送线控制 / CODESYS ST</span>
                <small>流程示意</small>
              </div>
              <ol className="rl-login-stages">
                {['需求确认', '计划审批', '代码生成', '编译验证'].map((stage, index) => (
                  <li key={stage} className={index === 3 ? 'rl-login-stage-current' : ''}>
                    <span>{index < 3 ? <Check size={16} aria-hidden="true" /> : '04'}</span>
                    <b>{stage}</b>
                  </li>
                ))}
              </ol>
              <div className="rl-login-workflow-footer"><span><i /><i /><i /></span>工程工件与诊断信息，持续关联<ArrowUpRight size={15} aria-hidden="true" /></div>
            </div>
            <div className="rl-login-assurances" aria-label="工程能力">
              <span><CircuitBoard size={14} aria-hidden="true" /> IEC 61131-3</span>
              <span><KeyRound size={14} aria-hidden="true" /> ST / SCL</span>
              <span><ShieldCheck size={14} aria-hidden="true" /> 安全校验</span>
            </div>
          </div>

          <div className="rl-login-agent">
            <div><strong>RealPLC Agent <span>· 本地验证连接</span></strong><small>让 PLC 代码进入 TIA Portal / CODESYS 原生编译</small></div>
            <a href="https://github.com/JadeHuang/RealPLC-Agent" target="_blank" rel="noopener noreferrer" aria-label="在 GitHub 查看 RealPLC Agent（新窗口）"><Github size={15} aria-hidden="true" /> GitHub <ArrowUpRight size={13} aria-hidden="true" /></a>
          </div>
        </section>

        <section className="rl-login-auth" aria-labelledby={`${fieldId}-heading`}>
          <header className="rl-login-auth-header">
            {onBack && <button className="rl-login-back" type="button" onClick={onBack}><ArrowLeft size={16} aria-hidden="true" /> 返回首页</button>}
            <span>REALPLC ACCOUNT</span>
          </header>

          <div className="rl-login-form-container">
            <p className="rl-login-eyebrow">WELCOME BACK <span /></p>
            <h1 id={`${fieldId}-heading`}>欢迎回来<span>.</span></h1>
            <p className="rl-login-subtitle">登录 RealPLC，继续你的工程项目。</p>

            <form className="rl-login-form" onSubmit={handleSubmit} noValidate aria-busy={busy}>
              <div className="rl-login-field">
                <label htmlFor={`${fieldId}-identifier`}>账号 / 邮箱</label>
                <div className={`rl-login-input-wrap${fieldErrors.identifier ? ' has-error' : ''}`}>
                  <UserRound size={18} aria-hidden="true" />
                  <input ref={identifierRef} id={`${fieldId}-identifier`} name="username" type="text"
                    value={identifier} onChange={(event) => { setIdentifier(event.target.value); setFieldErrors((errors) => ({ ...errors, identifier: '' })); setSubmitError(''); }}
                    placeholder="请输入账号或邮箱" autoComplete="username" autoCapitalize="none" spellCheck={false}
                    required disabled={busy} aria-invalid={Boolean(fieldErrors.identifier)}
                    aria-describedby={fieldErrors.identifier ? `${fieldId}-identifier-error` : undefined} />
                </div>
                {fieldErrors.identifier && <p className="rl-login-field-error" id={`${fieldId}-identifier-error`} role="alert">{fieldErrors.identifier}</p>}
              </div>

              <div className="rl-login-field">
                <label htmlFor={`${fieldId}-password`}>密码</label>
                <div className={`rl-login-input-wrap${fieldErrors.password ? ' has-error' : ''}`}>
                  <LockKeyhole size={18} aria-hidden="true" />
                  <input ref={passwordRef} id={`${fieldId}-password`} name="password" type={showPassword ? 'text' : 'password'}
                    value={password} onChange={(event) => { setPassword(event.target.value); setFieldErrors((errors) => ({ ...errors, password: '' })); setSubmitError(''); }}
                    onKeyDown={updateCapsLock} onKeyUp={updateCapsLock} onBlur={() => setCapsLock(false)}
                    placeholder="请输入登录密码" autoComplete="current-password" required disabled={busy}
                    aria-invalid={Boolean(fieldErrors.password)}
                    aria-describedby={[fieldErrors.password ? `${fieldId}-password-error` : '', capsLock ? `${fieldId}-caps-lock` : ''].filter(Boolean).join(' ') || undefined} />
                  <button className="rl-login-toggle" type="button" onClick={() => setShowPassword((value) => !value)}
                    aria-label={showPassword ? '隐藏密码' : '显示密码'} aria-pressed={showPassword}
                    aria-controls={`${fieldId}-password`} disabled={busy}>
                    {showPassword ? <EyeOff size={18} aria-hidden="true" /> : <Eye size={18} aria-hidden="true" />}
                  </button>
                </div>
                {fieldErrors.password && <p className="rl-login-field-error" id={`${fieldId}-password-error`} role="alert">{fieldErrors.password}</p>}
                {capsLock && <p className="rl-login-caps" id={`${fieldId}-caps-lock`} role="status">大写锁定已开启，请留意密码大小写。</p>}
              </div>

              {error && <div className="rl-login-error" role="alert"><AlertCircle size={17} aria-hidden="true" /><span>{error}</span></div>}

              <button className="rl-login-submit" type="submit" disabled={busy}>
                {busy ? <><Loader2 size={18} className="rl-login-spinner" aria-hidden="true" /> 正在登录…</> : <>登录 RealPLC <ArrowRight size={18} aria-hidden="true" /></>}
              </button>
              <p className="rl-login-status" role="status" aria-live="polite">{busy ? '正在验证账号，请稍候。' : ''}</p>
            </form>

            <p className="rl-login-security"><ShieldCheck size={15} aria-hidden="true" /> 统一身份认证 · 连接你的工程工作区</p>
            <div className="rl-login-account-help">
              <div className="rl-login-register-heading"><span>还没有 RealPLC 账号？</span><small>内测申请</small></div>
              <a className="rl-login-register" href="https://mp.weixin.qq.com/s/7nWms7Ew6e6Q6DePu2Oj8A" target="_blank" rel="noopener noreferrer">
                申请注册 <ArrowUpRight size={16} aria-hidden="true" />
                <span className="rl-login-status">（在新窗口查看内测申请说明）</span>
              </a>
              <p>查看内测申请说明，了解账号开通方式。</p>
            </div>
          </div>

          <footer className="rl-login-footer"><span>© {new Date().getFullYear()} RealPLC</span>{versionLabel && <span aria-label={`RealPLC 当前版本 ${versionLabel}`}>版本 <b>{versionLabel}</b></span>}</footer>
        </section>
      </div>
    </main>
  );
}

export default LoginGatewayPageV3;
